import { FileBrowserType } from '@common/Configuration/types';
import { useFilePathPsetsData } from '@pages/Transpiler/useFilePathPsetsData';
import { theme } from '@prophecy/theme';
import {
  Button,
  Centered,
  Ellipsis,
  FileBrowser,
  Separator,
  SlidingPanel,
  Spinner,
  Stack,
  StackItem,
  Text
} from '@prophecy/ui-v3';
import { getFileBrowserData } from '@prophecy/ui-v3/FileBrowser/util';
import { TABLE_CONTAINER } from '@prophecy/ui-v3/Table/tokens';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useImportState } from '../../useImportState';
import { searchAndFilterWithExtension } from '../utils';
import { SearchFileBrowserView } from './SearchFileBrowserView';

const StyledSlidingPanel = styled(SlidingPanel)`
  &&& .${TABLE_CONTAINER} {
    padding: 0;
    border-radius: ${theme.radius.x4};
  }
`;

type Props = FileBrowserType & {
  inputLabel?: string;
  pathToMove?: string;
  onSubmit?: (path: string) => void;
  loading?: boolean;
  extension?: string;
  psetList?: string[];
  parentPsetPath?: string;
};
export const TranspilerFileBrowser = ({
  selectedPath,
  loading = false,
  onChange,
  parentPsetPath = '',
  onSubmit,
  toggleFilePath,
  pathToMove,
  psetList = [],
  extension,
  inputLabel = 'Selected File'
}: Props) => {
  const [path, setPath] = useState(selectedPath || '');

  const {
    transpilerState: { uploadedFilePaths }
  } = useImportState();

  const { pipelinePsets, isLoading } = useFilePathPsetsData(parentPsetPath, !parentPsetPath);

  const { filteredPaths } = useMemo(
    () =>
      searchAndFilterWithExtension(
        parentPsetPath && pipelinePsets?.length ? pipelinePsets : uploadedFilePaths,
        '',
        extension ? [extension] : undefined,
        undefined,
        pathToMove
      ),
    [uploadedFilePaths, parentPsetPath, pipelinePsets, extension, pathToMove]
  );

  const filteredData = useMemo(
    () => getFileBrowserData(filteredPaths, path, !!pathToMove),
    [filteredPaths, path, pathToMove]
  );
  const handleSubmit = async () => {
    onChange(path);
    if (pathToMove) {
      await onSubmit?.(path);
    }
    toggleFilePath();
  };
  const selectedPathExtension = path.split('.');
  const isDuplicate = Boolean(psetList?.includes(path) && path !== selectedPath && path !== '');
  const isPathMatched = path === pathToMove;
  const isDisabled =
    (extension ? selectedPathExtension[selectedPathExtension.length - 1] !== extension : false) ||
    isDuplicate ||
    isPathMatched;

  const isPathToMoveDisabled = isPathMatched || !path;

  const footer = (
    <Stack direction='horizontal' width='100%' align='space-between' alignY='center'>
      <Button
        loading={loading}
        variant='secondaryGray'
        onClick={() => toggleFilePath?.()}
        data-test-id='transpiler-file-browser-close-button'>
        Close
      </Button>
      <Button
        loading={loading}
        onClick={handleSubmit}
        disabled={pathToMove ? isPathToMoveDisabled : isDisabled}
        data-test-id='transpiler-file-browser-submit-button'>
        {pathToMove ? 'Move' : 'Done'}
      </Button>
    </Stack>
  );

  const extensionText = `${extension ? `${extension}s` : 'psets'}`;
  const emptyText = pathToMove
    ? `No folder found. Please upload a folder and then move the file.`
    : `No relevant ${extensionText} were found for this pipeline. Please navigate to 'All files' section in previous screen to add respective ${extensionText}.`;

  return (
    <StyledSlidingPanel open={true} onClose={() => toggleFilePath()} footer={footer}>
      <Stack onClick={(e) => e.preventDefault()} gap={theme.spaces.x12} height='100%' width='100%'>
        <Text
          level='md'
          hint={
            parentPsetPath && pipelinePsets?.length > 0
              ? `Below is a list of recommended file paths only. In case you are not able to find your file, please select it from the 'All files' section in the previous screen.`
              : undefined
          }>
          {inputLabel}
        </Text>
        <Stack width='100%' padding={`${theme.spaces.x10} 0`}>
          <Separator />
        </Stack>

        {pathToMove && (
          <>
            <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
              <Text level='xs' tone={theme.colors.gray500}>
                Source Path:
              </Text>
              <StackItem grow='1'>
                <Ellipsis tooltip={true}>
                  <Text level='xs' tone={theme.colors.gray500}>
                    {pathToMove}
                  </Text>
                </Ellipsis>
              </StackItem>
            </Stack>
            <Text level='xs' tone={theme.colors.warning700}>
              Note: Moving {pathToMove} path may impact the entities (Job/Pipeline/Pset/Path) chosen for transpilation.
              We recommend to validate entities before transpilation after moving this path.
            </Text>
          </>
        )}
        <SearchFileBrowserView showFoldersOnly={!!pathToMove} paths={filteredPaths} setSelectedPath={setPath} />
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
          <Text level='xs' tone={theme.colors.gray500}>
            {pathToMove ? 'Target ' : 'File '} Path:
          </Text>
          <StackItem grow='1'>
            <Ellipsis tooltip={true}>
              <Text level='xs' tone={theme.colors.gray500}>
                {path}
              </Text>
            </Ellipsis>
          </StackItem>
        </Stack>
        {isDuplicate && (
          <Text level='xs' tone={theme.colors.error500}>
            This Parameter Set Path already exists.
          </Text>
        )}

        <StackItem grow='1'>
          {isLoading && parentPsetPath ? (
            <Stack width='100%' height='100%' align='center' alignY='center'>
              <Spinner spinning={true} />
            </Stack>
          ) : filteredData.length > 0 ? (
            <FileBrowser
              virtualize={true}
              emptyText={emptyText}
              directoryList={filteredData}
              selectedPath={path}
              onFileClick={(file) => setPath(file.path)}
            />
          ) : (
            <Centered>
              <Text level='sm15'>{emptyText}</Text>
            </Centered>
          )}
        </StackItem>
      </Stack>
    </StyledSlidingPanel>
  );
};
